//
import { fromJS } from 'immutable';
import * as ldClient from 'launchdarkly-js-client-sdk';
import camelCase from 'lodash/camelCase';

import { authSelectors } from 'app-components/flux/auth';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { userInfoSelectors } from 'app-components/flux/user-info';

import { featureFlagsActions, featureFlagsSimplifi } from 'app-components/flux/feature-flags';
import { getBuildConfig, getLogger, localPreferences } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import buildUser from './user';
import { entitlementsSelectors } from 'app-components/flux/entitlements';

const log = getLogger('featureFlags/initialize');

const preProcessFlags = (flags) => {
  const flagsResult = {};
  let featureFlagsOverrides = {};
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  featureFlagsOverrides = localPreferences.getItem('featureFlagsOverrides') || {};
///////////
  Object.entries(flags).forEach(([key, value]) => {
    const camelCaseKey = camelCase(key);
    flagsResult[camelCaseKey] = Object.prototype.hasOwnProperty.call(featureFlagsOverrides || {}, camelCaseKey)
      ? featureFlagsOverrides?.[camelCaseKey]
      : fromJS(value);
  });
  return flagsResult;
};

export const setFeatureFlags = (featureFlagsDefaults, meta) => {
  const flags = preProcessFlags(featureFlagsDefaults);
  store.dispatch(featureFlagsActions.applyFeatureFlagsChanges(flags, meta));
};

export function initializeFeatureFlags() {
  if (!getBuildConfig().ld_client_id) {
    log.warn("Couldn't find ld_client_id in configuration data, so not initializing LaunchDarkly");
    return;
  }
  if (!store) {
    log.warn('Store missing when LD init attempted!');
    return;
  }
  const state = store.getState();
  if (state.featureFlagsStore.loadPending) {
    const isBiz = entitlementsSelectors.getBusinessEnabled(state);
    setFeatureFlags(
      isBiz ? featureFlagsSimplifi.featureFlagsSimplifiQBPDefaults : featureFlagsSimplifi.featureFlagsSimplifiDefaults,
      { context: 'init' },
    );
  }
  const authSession = authSelectors.getAuthSession(state);

  const subscriptionTrackingProps = subscriptionsSelectors.getSubscriptionTrackingProps(state, authSession?.datasetId);
  const userInfo = userInfoSelectors.getUserInfo(state);
  featureFlagsIdentifyUser(authSession, subscriptionTrackingProps, userInfo);
}

export function featureFlagsIdentifyUser(authSession, subscriptionTrackingProps, userInfo) {
  if (!authSession || !authSession.qcsId) {
    return;
  }
  const user = buildUser(authSession, subscriptionTrackingProps, userInfo);
  log.debug('\nLaunch Darkly identify: \n', user);
  if (!window.ldClient) {
    initializeLdClient(user);
  } else {
    try {
      window.ldClient.identify(user, null, () => {});
    } catch (e) {
      log.error('Exception when updating user in LaunchDarkly', e);
    }
  }
}

export function initializeLdClient(user) {
  if (!localPreferences.getFeatureFlagsFreeze()) {
    const options = { bootstrap: 'localStorage' };
    log.debug('Launch Darkly - Initializing with', getBuildConfig().ld_client_id, user, options);
    try {
      store.dispatch(featureFlagsActions.getFeatureFlags());
      window.ldClient = ldClient.initialize(getBuildConfig().ld_client_id, user, options);
      window.ldClient.on('ready', updateAllFlags);
      window.ldClient.on('change', (flags) => {
        log.debug('Launch Darkly - Received feature flags on change', flags);
        const flagsToSet = {};
        Object.entries(flags).forEach(([key, value]) => {
          // $FlowFixMe
          flagsToSet[key] = value.current;
        });
        setFeatureFlags(flagsToSet, { context: 'update' });
      });
    } catch (e) {
      log.error('Exception received when initializing feature flags', e);
    }
  }
}

export function updateAllFlags() {
  const flags = window?.ldClient?.allFlags?.();
  if (flags && Object.keys(flags).length) {
    log.debug('Launch Darkly - received feature flags on ready', flags);
    const flagsResult = preProcessFlags(flags);
    store.dispatch(featureFlagsActions.getFeatureFlagsSuccess(flagsResult, { context: 'on ready' }));
  } else {
    log.debug('Launch Darkly - No feature flags received on ready', flags);
    store.dispatch(featureFlagsActions.getFeatureFlagsFailure(Error('No feature flags recieved')));
  }
}
